// Filters for a section's list, as removable chips, and personal saved views of them
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Bookmark, ListFilter, Trash2, X } from "lucide-react"

export interface FilterDef {
  key: string
  label: string
  options: { value: string; label: string }[]
}

export type FilterValue = Record<string, string>

export interface SavedView {
  name: string
  filters: FilterValue
}

// Saved views are per person and per section, kept in this browser
const viewsKey = (section: string) => `smart-cms-views:${section}`
export const readViews = (section: string): SavedView[] => {
  try { return JSON.parse(localStorage.getItem(viewsKey(section)) || "[]") } catch { return [] }
}
export const writeViews = (section: string, views: SavedView[]) => {
  try { localStorage.setItem(viewsKey(section), JSON.stringify(views)) } catch { /* private window */ }
}

// A list's filters, for the assistant to read and set while the list is on screen
export interface FilterTarget {
  defs: FilterDef[]
  value: FilterValue
  onChange: (next: FilterValue) => void
}
const targets = new Map<string, FilterTarget>()
export const filterTarget = (section: string) => targets.get(section)

export function FilterBar({ section, defs, value, onChange }: {
  section: string
  defs: FilterDef[]
  value: FilterValue
  onChange: (next: FilterValue) => void
}) {
  const [open, setOpen] = useState(false)
  const [viewsOpen, setViewsOpen] = useState(false)
  const [views, setViews] = useState<SavedView[]>(() => readViews(section))
  const [naming, setNaming] = useState("")
  const active = defs.filter(d => value[d.key])
  // Registered while mounted; refreshed each render so the assistant sees current options
  targets.set(section, { defs, value, onChange })
  useEffect(() => () => { if (targets.get(section)?.onChange === onChange) targets.delete(section) }, [section, onChange])
  // Views the assistant saved show up the next time the menu opens
  useEffect(() => { if (viewsOpen) setViews(readViews(section)) }, [viewsOpen, section])

  const set = (key: string, v: string) => {
    const next = { ...value }
    if (v === "any") delete next[key]
    else next[key] = v
    onChange(next)
  }
  const saveView = () => {
    const name = naming.trim()
    if (!name) return
    const next = [...views.filter(v => v.name !== name), { name, filters: value }]
    setViews(next)
    writeViews(section, next)
    setNaming("")
  }
  const removeView = (name: string) => {
    const next = views.filter(v => v.name !== name)
    setViews(next)
    writeViews(section, next)
  }

  return (
    <div className="flex flex-wrap items-center gap-1 border-b px-2 py-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-xs">
            <ListFilter className="h-3.5 w-3.5" /> Filter{active.length ? ` (${active.length})` : ""}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-2 p-3">
          {defs.map(d => (
            <label key={d.key} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-[11px] font-medium">{d.label}</span>
              <Select value={value[d.key] ?? "any"} onValueChange={v => set(d.key, v)}>
                <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any" className="text-xs">Any</SelectItem>
                  {d.options.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          ))}
          {active.length > 0 && (
            <Button size="sm" variant="ghost" className="h-7 w-full text-xs" onClick={() => onChange({})}>Clear filters</Button>
          )}
        </PopoverContent>
      </Popover>

      {active.map(d => (
        <span key={d.key} className="inline-flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-[11px] text-primary">
          {d.label}: {d.options.find(o => o.value === value[d.key])?.label ?? value[d.key]}
          <button type="button" className="rounded-full p-0.5 hover:bg-primary/15" onClick={() => set(d.key, "any")} aria-label={`Remove ${d.label} filter`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      <Popover open={viewsOpen} onOpenChange={setViewsOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" className="ml-auto h-6 gap-1 px-1.5 text-xs text-muted-foreground" title="Saved views">
            <Bookmark className="h-3.5 w-3.5" /> Views
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2">
          {views.length === 0 && <p className="px-1 py-1.5 text-[11px] text-muted-foreground">No saved views yet.</p>}
          {views.map(v => (
            <div key={v.name} className="group flex items-center gap-1 rounded hover:bg-muted">
              <button type="button" className="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-xs" onClick={() => { onChange(v.filters); setViewsOpen(false) }}>
                {v.name}
              </button>
              <button type="button" className="p-1.5 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100" onClick={() => removeView(v.name)} aria-label={`Delete view ${v.name}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {active.length > 0 && (
            <form className="mt-1 flex gap-1 border-t pt-2" onSubmit={e => { e.preventDefault(); saveView() }}>
              <Input value={naming} onChange={e => setNaming(e.target.value)} placeholder="Name this view" className="h-7 text-xs" />
              <Button type="submit" size="sm" className="h-7 px-2 text-xs" disabled={!naming.trim()}>Save</Button>
            </form>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

// What the page knows about a record beyond its content, for the shared filters
export interface RecordInfo {
  pending: boolean
  review: "ok" | "soon" | "overdue" | null
  ownerId?: string
}

// Filters every section offers: unpublished changes, review dates and owner
export function workflowFilterDefs(people: { id: string; name: string }[]): FilterDef[] {
  return [
    { key: "changes", label: "Changes", options: [{ value: "pending", label: "Not live yet" }] },
    { key: "review", label: "Review", options: [{ value: "overdue", label: "Overdue" }, { value: "due", label: "Due or overdue" }] },
    { key: "owner", label: "Owner", options: [{ value: "none", label: "No owner" }, ...people.map(p => ({ value: p.id, label: p.name }))] },
  ]
}

export function matchesWorkflow(info: RecordInfo | undefined, f: FilterValue): boolean {
  if (f.changes === "pending" && !info?.pending) return false
  if (f.review === "overdue" && info?.review !== "overdue") return false
  if (f.review === "due" && info?.review !== "overdue" && info?.review !== "soon") return false
  if (f.owner === "none" && info?.ownerId) return false
  if (f.owner && f.owner !== "none" && info?.ownerId !== f.owner) return false
  return true
}
